import { ReconciliationFilePreview } from "@/components/reconciliation/file-preview";
import { ReconciliationStatusBadge } from "@/components/reconciliation/reconciliation-status-badge";
import {
  formatCurrency,
  formatDate,
  formatRunId,
  getRunTitle,
} from "@/components/reconciliation/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { buildMetadata } from "@/lib/metadata";
import { ProductService } from "@/services/product-service";
import {
  ReconciliationService,
  type ReconciliationInvoiceUpdatePayload,
} from "@/services/reconciliation-service";
import { UserProfileService } from "@/services/user-profile-service";
import type { ReconciliationInvoice } from "@/services/types/reconciliation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Download,
  FileImage,
  FileText,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 10;
const ACTIVE_PAGE_SELECTOR_BG = "#FDEB7D";
const ACTIVE_PAGE_SELECTOR_TEXT = "#2D0909";

export const Route = createFileRoute("/_main/reconciliation/$runId/")({
  beforeLoad: async () => {
    const profile = await UserProfileService.getMine();
    if (!profile?.is_financial_reconciliation_enabled) {
      throw redirect({ to: "/" });
    }
  },
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "Reconciliation run detail",
      description: "Review invoice-level results and approve completed reconciliation runs.",
      keywords: ["reconciliation detail", "invoice review"],
    }),
  }),
});

function SummaryCard({
  label,
  value,
  detail,
  dotClassName,
}: {
  label: string;
  value: string;
  detail: string;
  dotClassName: string;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200 py-0 shadow-none">
      <CardHeader className="px-5 pt-5">
        <CardTitle className="flex items-center gap-2 text-base text-slate-500">
          <span className={`size-2 rounded-full ${dotClassName}`} />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <div className="text-4xl tracking-tight">{value}</div>
        <div className="mt-2 text-sm text-slate-400">{detail}</div>
      </CardContent>
    </Card>
  );
}

function normalizeInvoiceStatusFilter(status: string) {
  if (["awaiting_reconciliation", "pending_bank_statements"].includes(status)) {
    return "extracted";
  }
  return status;
}

function buildPageItems(currentPage: number, totalPages: number) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      "ellipsis",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "ellipsis-left",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "ellipsis-right",
    totalPages,
  ];
}

function RouteComponent() {
  const { runId } = Route.useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);

  const { data: run, isLoading: runLoading } = useQuery({
    queryKey: ["reconciliation-run", runId],
    queryFn: () => ReconciliationService.getRun(runId),
    refetchInterval: (query) => {
      const current = query.state.data;
      return current &&
        [
          "invoice_processing",
          "bank_statement_processing",
          "ready_to_reconcile",
        ].includes(current.run_status)
        ? 5000
        : false;
    },
  });

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ["reconciliation-invoices", runId],
    queryFn: () => ReconciliationService.listInvoices(runId),
    enabled: Boolean(runId),
  });

  const approveMutation = useMutation({
    mutationFn: () => ReconciliationService.approveRun(runId),
    onSuccess: async () => {
      toast.success("Run approved", {
        description: "This reconciliation run is now locked.",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reconciliation-run", runId] }),
        queryClient.invalidateQueries({ queryKey: ["reconciliation-runs"] }),
      ]);
    },
    onError: (error) => {
      toast.error("Approval blocked", {
        description:
          error instanceof Error ? error.message : "Resolve remaining invoices before approval.",
      });
    },
  });

  const visibleInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const haystack =
        `${invoice.invoice_number} ${invoice.supplier_name} ${invoice.supplier_name_raw}`.toLowerCase();
      if (search && !haystack.includes(search.toLowerCase())) {
        return false;
      }
      if (tab === "review" && !invoice.review_required) {
        return false;
      }
      if (vendorFilter !== "all") {
        const vendorName =
          invoice.supplier_name || invoice.supplier_name_raw || "Unknown";
        if (vendorName !== vendorFilter) {
          return false;
        }
      }
      if (
        statusFilter !== "all" &&
        normalizeInvoiceStatusFilter(invoice.reconciliation_status) !== statusFilter
      ) {
        return false;
      }
      return true;
    });
  }, [invoices, search, tab, vendorFilter, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, tab, vendorFilter]);

  useEffect(() => {
    if (
      expandedInvoiceId &&
      !visibleInvoices.some(
        (invoice) => String(invoice.id) === String(expandedInvoiceId),
      )
    ) {
      setExpandedInvoiceId(null);
    }
  }, [expandedInvoiceId, visibleInvoices]);

  const totalPages = Math.max(Math.ceil(visibleInvoices.length / PAGE_SIZE), 1);
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage);
    }
  }, [currentPage, page]);

  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return visibleInvoices.slice(start, start + PAGE_SIZE);
  }, [currentPage, visibleInvoices]);

  const pageItems = useMemo(
    () => buildPageItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const vendorOptions = useMemo(() => {
    const names = new Set(
      invoices.map(
        (invoice) =>
          invoice.supplier_name || invoice.supplier_name_raw || "Unknown",
      ),
    );
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [invoices]);

  const exportRows = () => {
    const lines = [
      ["Invoice ID", "Vendor", "Amount", "Currency", "Date", "Status"].join(","),
      ...invoices.map((invoice) =>
        [
          invoice.invoice_number || "",
          invoice.supplier_name || invoice.supplier_name_raw || "",
          invoice.amount ?? "",
          invoice.currency || "",
          invoice.date || "",
          invoice.reconciliation_status,
        ]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${formatRunId(runId)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (runLoading || !run) {
    return <div className="p-8 text-slate-500">Loading reconciliation run...</div>;
  }

  const canApprove = run.run_status === "ready_for_approval" && !run.is_approved;
  const extractedCount = invoices.filter(
    (invoice) => invoice.extraction_status === "completed",
  ).length;

  return (
    <div className="space-y-8 p-4 pb-32 lg:p-8 lg:pb-8">
      <Link
        to="/reconciliation"
        className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Financial Reconciliation
      </Link>

      <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="space-y-2">
          <div className="text-center text-sm text-slate-500 lg:text-left">
            {formatRunId(run.id)} | {run.counts.invoices} invoices | Started{" "}
            {formatDate(run.date_uploaded)}
          </div>
          <h1 className="text-center text-3xl tracking-tight lg:text-left lg:text-4xl">
            {getRunTitle(run)}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-3 lg:justify-start">
            <ReconciliationStatusBadge status={run.run_status} />
            {run.is_approved ? (
              <span className="text-sm text-slate-500">
                Approved on {formatDate(run.approved_at)}
              </span>
            ) : null}
          </div>
        </div>
        <div className="hidden flex-wrap gap-3 lg:flex">
          <Button variant="outline" className="rounded-xl" onClick={exportRows}>
            <Download className="size-4" />
            Export CSV
          </Button>
          <Button
            onClick={() => approveMutation.mutate()}
            disabled={!canApprove || approveMutation.isPending}
            className="rounded-xl bg-black px-6 text-white hover:bg-black/90"
          >
            {approveMutation.isPending ? "Approving..." : "Approve run"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <SummaryCard
          label="Invoices"
          value={String(run.counts.invoices)}
          detail="In this run"
          dotClassName="bg-black"
        />
        <SummaryCard
          label="Extracted"
          value={String(extractedCount)}
          detail="Invoices extracted"
          dotClassName="bg-emerald-500"
        />
        <SummaryCard
          label="Needs review"
          value={String(run.counts.needs_review)}
          detail="Low confidence or mismatch"
          dotClassName="bg-rose-500"
        />
        <SummaryCard
          label="Run total"
          value={formatCurrency(run.totals.invoice_total, "SAR")}
          detail="Sum of active invoices"
          dotClassName="bg-amber-700"
        />
      </div>

      <Card className="rounded-[24px] border-slate-200 py-0 shadow-none">
        <CardHeader className="px-6 pt-6">
          <div className="flex flex-col gap-4">
            <div className="border-slate-200 border-b">
              <div className="flex items-end gap-8">
                {[
                  { value: "all", label: "All invoices" },
                  { value: "review", label: "Needs review" },
                ].map((item) => {
                  const active = tab === item.value;

                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setTab(item.value)}
                      className={`relative pb-3 text-base transition-colors ${
                        active
                          ? "font-medium text-slate-950"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {item.label}
                      <span
                        className={`absolute inset-x-0 bottom-0 h-0.5 rounded-full transition-opacity ${
                          active ? "bg-foreground opacity-100" : "opacity-0"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="relative w-full xl:max-w-md">
                <Search className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search invoices by ID or vendor"
                  className="h-12 rounded-xl border-slate-200 pl-10"
                />
              </div>
              <div className="hidden flex-col gap-3 sm:flex-row lg:flex">
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="h-12 min-w-44 rounded-xl border-slate-200">
                    <SelectValue placeholder="All vendors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All vendors</SelectItem>
                    {vendorOptions.map((vendor) => (
                      <SelectItem key={vendor} value={vendor}>
                        {vendor}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-12 min-w-44 rounded-xl border-slate-200">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="in_review">In review</SelectItem>
                    <SelectItem value="mismatched">Mismatch</SelectItem>
                    <SelectItem value="extracted">Extracted</SelectItem>
                    <SelectItem value="excluded">Excluded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-6 pb-6">
          <div className="mb-4 text-sm text-slate-500">
            Showing {paginatedInvoices.length} of {visibleInvoices.length} invoices
          </div>
          <div className="space-y-3 lg:hidden">
            {invoicesLoading ? (
              <div className="rounded-[20px] border border-slate-200 px-5 py-16 text-center text-slate-400">
                Loading invoices...
              </div>
            ) : paginatedInvoices.length === 0 ? (
              <div className="rounded-[20px] border border-slate-200 px-5 py-16 text-center text-slate-400">
                No invoices match the current filters.
              </div>
            ) : (
              paginatedInvoices.map((invoice) => (
                <div
                  key={String(invoice.id)}
                  className="overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm"
                >
                  <InvoiceMobileCard
                    invoice={invoice}
                    expanded={expandedInvoiceId === String(invoice.id)}
                    onToggle={() =>
                      setExpandedInvoiceId((current) =>
                        current === String(invoice.id) ? null : String(invoice.id),
                      )
                    }
                  />
                  {expandedInvoiceId === String(invoice.id) ? (
                    <div className="border-t border-slate-200 p-4">
                      <div className="lg:hidden">
                        <InvoiceExpandedPanelMobile
                          runId={runId}
                          invoiceId={String(invoice.id)}
                        />
                      </div>
                      <div className="hidden lg:block">
                        <InvoiceExpandedPanel runId={runId} invoiceId={String(invoice.id)} />
                      </div>
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
          <div className="hidden overflow-hidden rounded-[20px] border border-slate-200 bg-white lg:block">
            <table className="w-full min-w-[980px] bg-white">
              <thead className="bg-slate-50 text-left text-sm text-slate-500">
                <tr>
                  <th className="px-5 py-4 font-medium">Image</th>
                  <th className="px-5 py-4 font-medium">Invoice ID</th>
                  <th className="px-5 py-4 font-medium">Vendor</th>
                  <th className="px-5 py-4 font-medium">Amount</th>
                  <th className="px-5 py-4 font-medium">Date</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium" />
                </tr>
              </thead>
              <tbody>
                {invoicesLoading ? (
                  <tr>
                    <td className="px-5 py-16 text-center text-slate-400" colSpan={7}>
                      Loading invoices...
                    </td>
                  </tr>
                ) : paginatedInvoices.length === 0 ? (
                  <tr>
                    <td className="px-5 py-16 text-center text-slate-400" colSpan={7}>
                      No invoices match the current filters.
                    </td>
                  </tr>
                ) : (
                  paginatedInvoices.map((invoice) => (
                    <InvoiceRow
                      key={String(invoice.id)}
                      runId={runId}
                      invoice={invoice}
                      expanded={expandedInvoiceId === String(invoice.id)}
                      onToggle={() =>
                        setExpandedInvoiceId((current) =>
                          current === String(invoice.id) ? null : String(invoice.id),
                        )
                      }
                    />
                  ))
                )}
              </tbody>
            </table>
            {!invoicesLoading && visibleInvoices.length > 0 ? (
              <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-xl border-slate-200 px-4"
                  onClick={() => setPage((value) => Math.max(value - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  {pageItems.map((item, index) =>
                    typeof item === "number" ? (
                      <button
                        key={`${item}-${index}`}
                        type="button"
                        onClick={() => setPage(item)}
                        className={`flex size-10 items-center justify-center rounded-lg transition ${
                          item === currentPage
                            ? "shadow-sm"
                            : "text-slate-500 hover:bg-slate-100"
                        }`}
                        style={
                          item === currentPage
                            ? {
                                backgroundColor: ACTIVE_PAGE_SELECTOR_BG,
                                color: ACTIVE_PAGE_SELECTOR_TEXT,
                              }
                            : undefined
                        }
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={`${item}-${index}`} className="px-1 text-slate-400">
                        ...
                      </span>
                    ),
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-xl border-slate-200 px-4"
                  onClick={() =>
                    setPage((value) => Math.min(value + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="fixed right-4 bottom-4 left-4 z-20 pb-[max(env(safe-area-inset-bottom),0px)] lg:hidden">
        <Button
          onClick={() => approveMutation.mutate()}
          disabled={!canApprove || approveMutation.isPending}
          className="h-12 w-full rounded-xl bg-black text-white hover:bg-black/90"
        >
          {approveMutation.isPending ? "Approving..." : "Approve run"}
        </Button>
      </div>
    </div>
  );
}

function InvoiceRow({
  runId,
  invoice,
  expanded,
  onToggle,
}: {
  runId: string;
  invoice: ReconciliationInvoice;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={`border-t border-slate-100 text-sm transition-colors hover:bg-slate-50 ${
          expanded ? "bg-slate-50" : ""
        }`}
      >
        <td className="px-5 py-5">
          <div className="flex size-12 items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
            {(invoice.source_file_name || "").toLowerCase().endsWith(".pdf") ? (
              <FileText className="size-5 text-slate-500" />
            ) : (
              <FileImage className="size-5 text-slate-500" />
            )}
          </div>
        </td>
        <td className="px-5 py-5">
          <button
            type="button"
            onClick={onToggle}
            className="block text-left"
          >
            <div className="font-medium text-slate-900">
              {invoice.invoice_number || "Pending extraction"}
            </div>
            <div className="text-slate-400">
              {invoice.source_file_name || "Unknown file"}
            </div>
          </button>
        </td>
        <td className="px-5 py-5 text-slate-700">
          {invoice.supplier_name || invoice.supplier_name_raw || "Unknown vendor"}
        </td>
        <td className="px-5 py-5 text-slate-900">
          {formatCurrency(invoice.amount, invoice.currency)}
        </td>
        <td className="px-5 py-5 text-slate-600">{formatDate(invoice.date)}</td>
        <td className="px-5 py-5">
          <ReconciliationStatusBadge status={invoice.reconciliation_status} />
        </td>
        <td className="px-5 py-5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={onToggle}
          >
            <ChevronRight className={`size-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </Button>
        </td>
      </tr>
      {expanded ? (
        <tr className="border-t border-slate-100 bg-slate-50/60">
          <td colSpan={7} className="p-5">
            <InvoiceExpandedPanel runId={runId} invoiceId={String(invoice.id)} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function InvoiceMobileCard({
  invoice,
  expanded,
  onToggle,
}: {
  invoice: ReconciliationInvoice;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button type="button" onClick={onToggle} className="block w-full p-4 text-left">
      <div className="flex items-start gap-3">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
          {(invoice.source_file_name || "").toLowerCase().endsWith(".pdf") ? (
            <FileText className="size-5 text-slate-500" />
          ) : (
            <FileImage className="size-5 text-slate-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-xl font-medium text-slate-900">
                {invoice.invoice_number || "Pending extraction"}
              </div>
              <div className="mt-1 truncate text-sm text-slate-400">
                {invoice.supplier_name || invoice.supplier_name_raw || "Unknown vendor"}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="font-medium text-slate-900">
                {formatCurrency(invoice.amount, invoice.currency)}
              </div>
              <div className="mt-2">
                <ReconciliationStatusBadge status={invoice.reconciliation_status} />
              </div>
            </div>
          </div>
        </div>
        <ChevronRight
          className={`mt-1 size-4 shrink-0 text-slate-400 transition-transform ${
            expanded ? "rotate-90" : ""
          }`}
        />
      </div>
    </button>
  );
}

function InvoiceExpandedPanel({
  runId,
  invoiceId,
}: {
  runId: string;
  invoiceId: string;
}) {
  const queryClient = useQueryClient();
  const { data: run } = useQuery({
    queryKey: ["reconciliation-run", runId],
    queryFn: () => ReconciliationService.getRun(runId),
  });
  const { data: invoice, isLoading } = useQuery({
    queryKey: ["reconciliation-invoice", runId, invoiceId],
    queryFn: () => ReconciliationService.getInvoice(runId, invoiceId),
  });
  const { data: customers = [] } = useQuery({
    queryKey: ["inventory-customers"],
    queryFn: () => ProductService.listCustomers({ pageSize: 500 }),
  });

  const [formState, setFormState] = useState({
    selectedCustomerContactId: "",
  });
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  useEffect(() => {
    if (!invoice) {
      return;
    }

    setFormState({
      selectedCustomerContactId:
        invoice.selected_customer_contact_id ||
        invoice.customer_match_candidates[0]?.contact_id ||
        "",
    });
    setShowMobilePreview(false);
  }, [invoice]);

  const sourceFile = useMemo(
    () =>
      run?.invoice_files.find(
        (file) => String(file.id) === String(invoice?.source_file_id),
      ) ?? null,
    [invoice?.source_file_id, run?.invoice_files],
  );

  const isLocked = Boolean(run?.is_approved || invoice?.run_locked);

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["reconciliation-run", runId] }),
      queryClient.invalidateQueries({ queryKey: ["reconciliation-runs"] }),
      queryClient.invalidateQueries({
        queryKey: ["reconciliation-invoices", runId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["reconciliation-invoice", runId, invoiceId],
      }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: ReconciliationInvoiceUpdatePayload = {
        selected_customer_contact_id:
          formState.selectedCustomerContactId || null,
      };

      return ReconciliationService.updateInvoice(runId, invoiceId, payload);
    },
    onSuccess: async () => {
      toast.success("Data saved successfully");
      await invalidateAll();
    },
    onError: (error) => {
      toast.error("Could not save invoice", {
        description:
          error instanceof Error
            ? error.message
            : "Please review the edited fields and try again.",
      });
    },
  });

  if (isLoading || !invoice) {
    return <div className="py-8 text-center text-slate-400">Loading invoice details...</div>;
  }

  const selectedCustomerDisplayName =
    invoice.selected_customer_contact_name ||
    invoice.customer_match_candidates[0]?.company_name ||
    invoice.customer_match_candidates[0]?.contact_name ||
    "";
  const suggestedCustomerIds = new Set(
    invoice.customer_match_candidates
      .map((candidate) => candidate.contact_id)
      .filter(Boolean),
  );
  const additionalCustomers = customers.filter(
    (customer) => !suggestedCustomerIds.has(customer.erp_contact_id),
  );
  const isCustomerSelectionDirty =
    (formState.selectedCustomerContactId || "") !==
    (invoice.selected_customer_contact_id ||
      invoice.customer_match_candidates[0]?.contact_id ||
      "");

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,.95fr)] xl:gap-5">
      <Card className="rounded-[24px] border-slate-200 py-0 shadow-none">
        <CardHeader className="px-5 pt-5">
          <CardTitle className="text-lg">Source invoice</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          <ReconciliationFilePreview file={sourceFile} className="min-h-[260px]" />
        </CardContent>
      </Card>

      <Card className="rounded-[24px] border-slate-200 py-0 shadow-none">
        <CardHeader className="px-5 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-lg">Extracted values</CardTitle>
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={isLocked || saveMutation.isPending || !isCustomerSelectionDirty}
              className="rounded-xl bg-black px-5 text-white hover:bg-black/90"
            >
              {saveMutation.isPending ? "Saving..." : "Save customer"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 px-5 pb-5 md:grid-cols-2">
          <ReadOnlyField
            label="Invoice number"
            value={invoice.invoice_number || "Pending extraction"}
          />
          <ReadOnlyField
            label="Vendor"
            value={invoice.supplier_name_raw || invoice.supplier_name || "Unknown vendor"}
          />
          <InvoiceCustomerSelector
            selectedCustomerContactId={formState.selectedCustomerContactId}
            selectedCustomerDisplayName={selectedCustomerDisplayName}
            candidates={invoice.customer_match_candidates}
            additionalCustomers={additionalCustomers}
            disabled={isLocked}
            onSelect={(value) =>
              setFormState((current) => ({
                ...current,
                selectedCustomerContactId: value,
              }))
            }
          />
          <ReadOnlyField
            label="Amount"
            value={formatCurrency(invoice.amount, invoice.currency)}
          />
          <ReadOnlyField
            label="Currency"
            value={invoice.currency || "-"}
          />
          <ReadOnlyField
            label="Date"
            value={formatDate(invoice.date)}
          />
          <div className="space-y-2 md:col-span-2">
            <Label>Description</Label>
            <div className="min-h-28 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              {invoice.description || "-"}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function InvoiceExpandedPanelMobile({
  runId,
  invoiceId,
}: {
  runId: string;
  invoiceId: string;
}) {
  const queryClient = useQueryClient();
  const { data: run } = useQuery({
    queryKey: ["reconciliation-run", runId],
    queryFn: () => ReconciliationService.getRun(runId),
  });
  const { data: invoice, isLoading } = useQuery({
    queryKey: ["reconciliation-invoice", runId, invoiceId],
    queryFn: () => ReconciliationService.getInvoice(runId, invoiceId),
  });
  const { data: customers = [] } = useQuery({
    queryKey: ["inventory-customers"],
    queryFn: () => ProductService.listCustomers({ pageSize: 500 }),
  });

  const [formState, setFormState] = useState({
    selectedCustomerContactId: "",
  });
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!invoice) {
      return;
    }

    setFormState({
      selectedCustomerContactId:
        invoice.selected_customer_contact_id ||
        invoice.customer_match_candidates[0]?.contact_id ||
        "",
    });
    setShowPreview(false);
  }, [invoice]);

  const sourceFile = useMemo(
    () =>
      run?.invoice_files.find(
        (file) => String(file.id) === String(invoice?.source_file_id),
      ) ?? null,
    [invoice?.source_file_id, run?.invoice_files],
  );

  const isLocked = Boolean(run?.is_approved || invoice?.run_locked);

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["reconciliation-run", runId] }),
      queryClient.invalidateQueries({ queryKey: ["reconciliation-runs"] }),
      queryClient.invalidateQueries({
        queryKey: ["reconciliation-invoices", runId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["reconciliation-invoice", runId, invoiceId],
      }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      ReconciliationService.updateInvoice(runId, invoiceId, {
        selected_customer_contact_id: formState.selectedCustomerContactId || null,
      }),
    onSuccess: async () => {
      toast.success("Data saved successfully");
      await invalidateAll();
    },
    onError: (error) => {
      toast.error("Could not save invoice", {
        description:
          error instanceof Error
            ? error.message
            : "Please review the edited fields and try again.",
      });
    },
  });

  if (isLoading || !invoice) {
    return <div className="py-6 text-center text-slate-400">Loading invoice details...</div>;
  }

  const selectedCustomerDisplayName =
    invoice.selected_customer_contact_name ||
    invoice.customer_match_candidates[0]?.company_name ||
    invoice.customer_match_candidates[0]?.contact_name ||
    "";
  const suggestedCustomerIds = new Set(
    invoice.customer_match_candidates
      .map((candidate) => candidate.contact_id)
      .filter(Boolean),
  );
  const additionalCustomers = customers.filter(
    (customer) => !suggestedCustomerIds.has(customer.erp_contact_id),
  );
  const isCustomerSelectionDirty =
    (formState.selectedCustomerContactId || "") !==
    (invoice.selected_customer_contact_id ||
      invoice.customer_match_candidates[0]?.contact_id ||
      "");

  return (
    <div className="space-y-4">
      <div className="rounded-[16px] border border-slate-200 bg-white p-4">
        <h3 className="text-lg font-semibold text-slate-950">Extracted values</h3>

        <div className="mt-4 space-y-4">
          <MobileField
            label="Invoice number"
            value={invoice.invoice_number || "Pending extraction"}
          />
          <MobileField
            label="Vendor"
            value={invoice.supplier_name_raw || invoice.supplier_name || "Unknown vendor"}
          />

          <div className="space-y-2">
            <Label>Customer</Label>
            <InvoiceCustomerSelector
              selectedCustomerContactId={formState.selectedCustomerContactId}
              selectedCustomerDisplayName={selectedCustomerDisplayName}
              candidates={invoice.customer_match_candidates}
              additionalCustomers={additionalCustomers}
              disabled={isLocked}
              onSelect={(value) =>
                setFormState((current) => ({
                  ...current,
                  selectedCustomerContactId: value,
                }))
              }
            />
          </div>

          <MobileField
            label="Amount"
            value={formatCurrency(invoice.amount, invoice.currency)}
          />
          <MobileField label="Currency" value={invoice.currency || "-"} />
          <MobileField label="Date" value={formatDate(invoice.date)} />

          <div className="space-y-2">
            <Label>Description</Label>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              {invoice.description || "-"}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Source invoice</Label>
              <Button
                type="button"
                variant="outline"
                className="h-8 rounded-lg px-3 text-xs"
                onClick={() => setShowPreview((current) => !current)}
              >
                {showPreview ? "Hide preview" : "Preview"}
              </Button>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              {sourceFile?.original_filename || invoice.source_file_name || "No source file available"}
            </div>
            {showPreview ? (
              <ReconciliationFilePreview file={sourceFile} className="min-h-[180px]" />
            ) : null}
          </div>

          <Button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={isLocked || saveMutation.isPending || !isCustomerSelectionDirty}
            className="h-12 w-full rounded-xl bg-black text-white hover:bg-black/90"
          >
            {saveMutation.isPending ? "Saving..." : "Save customer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={value} disabled className="h-12 rounded-xl border-slate-200" />
    </div>
  );
}

function MobileField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        {value}
      </div>
    </div>
  );
}

function InvoiceCustomerSelector({
  selectedCustomerContactId,
  selectedCustomerDisplayName,
  candidates,
  additionalCustomers,
  disabled,
  onSelect,
}: {
  selectedCustomerContactId: string;
  selectedCustomerDisplayName: string;
  candidates: ReconciliationInvoice["customer_match_candidates"];
  additionalCustomers: Awaited<ReturnType<typeof ProductService.listCustomers>>;
  disabled: boolean;
  onSelect: (value: string) => void;
}) {
  const buttonLabel = selectedCustomerDisplayName || "Select customer";

  return (
    <div className="space-y-2">
      <Label>Customer</Label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            className="h-12 w-full justify-between rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 shadow-sm hover:bg-slate-50"
            title={buttonLabel}
          >
            <span className="truncate">{buttonLabel}</span>
            <ChevronDown className="size-4 text-slate-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[var(--radix-dropdown-menu-trigger-width)] max-w-[calc(100vw-3rem)]"
        >
          <DropdownMenuItem onSelect={() => onSelect("")}>
            <div className="flex w-full items-center justify-between gap-2">
              <span className="font-medium text-xs">No customer selected</span>
              {!selectedCustomerContactId ? (
                <Badge className="rounded-full bg-brand-yellowGreen px-2 py-0 text-[10px] text-brand-darkGreen">
                  Selected
                </Badge>
              ) : null}
            </div>
          </DropdownMenuItem>
          {candidates.map((candidate) => {
            const active = selectedCustomerContactId === candidate.contact_id;
            const label =
              candidate.company_name ||
              candidate.contact_name ||
              candidate.contact_id;

            return (
              <DropdownMenuItem
                key={`suggested-${candidate.contact_id}`}
                className="flex flex-col items-start gap-1 py-2"
                onSelect={() => onSelect(candidate.contact_id)}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="font-medium text-xs">{label}</span>
                  <div className="flex items-center gap-2">
                    {candidate.similarity_score !== null ? (
                      <Badge variant="secondary" className="rounded-full px-2 py-0 text-[10px]">
                        {candidate.similarity_score}%
                      </Badge>
                    ) : null}
                    {active ? (
                      <Badge className="rounded-full bg-brand-yellowGreen px-2 py-0 text-[10px] text-brand-darkGreen">
                        Selected
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })}
          {additionalCustomers.map((customer) => {
            const active = selectedCustomerContactId === customer.erp_contact_id;
            const label =
              customer.company_name || customer.contact_name || customer.erp_contact_id;

            return (
              <DropdownMenuItem
                key={`customer-${customer.id}`}
                className="flex flex-col items-start gap-1 py-2"
                onSelect={() => onSelect(customer.erp_contact_id)}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="font-medium text-xs">{label}</span>
                  {active ? (
                    <Badge className="rounded-full bg-brand-yellowGreen px-2 py-0 text-[10px] text-brand-darkGreen">
                      Selected
                    </Badge>
                  ) : null}
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
